import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Chip,
  Divider,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PageLayout from "../../components/layout/PageLayout";
import { useGetPaymentAccountByIdQuery } from "../../store/api";
import { toast } from "react-toastify";
import { PAYMENT_ACCOUNTS_LIST_PATH } from "../../navigations/path";

const DetailItem = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography
      variant="body2"
      sx={{ color: "text.secondary", mb: 0.5, fontWeight: 500 }}
    >
      {label}
    </Typography>

    <Typography variant="body1" sx={{ fontWeight: 500 }}>
      {value !== null && value !== undefined && value !== "" ? value : "-"}
    </Typography>
  </Grid>
);

const PaymentAccountDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data,
    isLoading: loading,
    isError,
    error,
  } = useGetPaymentAccountByIdQuery(id, { skip: !id });

  const paymentAccount = useMemo(() => data?.data ?? null, [data]);

  useEffect(() => {
    if (isError) {
      toast.error(
        error?.data?.message || "Failed to fetch payment account details",
      );
    }
  }, [isError, error]);

  const handleBack = () => {
    navigate(PAYMENT_ACCOUNTS_LIST_PATH);
  };

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ mb: 3, fontWeight: 600, color: "primary.main" }}
            >
              Payment Account Details
            </Typography>
          </Box>
          {/* Back button */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Back to Payment Accounts
            </Button>
          </Box>
        </Box>

        <Card>
          <CardContent sx={{ p: 3 }}>
            {loading ? (
              <Box
                sx={{
                  minHeight: 250,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : !paymentAccount ? (
              <Typography>No payment account found.</Typography>
            ) : (
              <>
                <Grid container spacing={3}>
                  {/* <DetailItem label="ID" value={paymentAccount.id} /> */}
                  <DetailItem
                    label="Account Name"
                    value={paymentAccount.name}
                  />
                  <DetailItem
                    label="Descriptor"
                    value={paymentAccount.descriptor}
                  />
                  <DetailItem
                    label="Daily Limit"
                    value={paymentAccount.daily_limit}
                  />
                  <DetailItem
                    label="Monthly Limit"
                    value={paymentAccount.monthly_limit}
                  />
                  <DetailItem label="Status" value={paymentAccount.status} />
                  <DetailItem
                    label="Max Allowed"
                    value={paymentAccount.max_allowed}
                  />
                  <DetailItem
                    label="Created At"
                    value={paymentAccount.created_at}
                  />

                  {/* Payment Type */}
                  <DetailItem
                    label="Payment Type"
                    value={paymentAccount?.payment_type?.name}
                  />

                  {/* Payment Gateway */}
                  <DetailItem
                    label="Payment Gateway"
                    value={paymentAccount?.payment_gateway?.name}
                  />
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Data keys */}
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Account Data Keys
                </Typography>

                {Array.isArray(paymentAccount?.data) &&
                paymentAccount.data.length > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {paymentAccount.data.map((item, index) => (
                      <Chip key={index} label={item} variant="outlined" />
                    ))}
                  </Stack>
                ) : (
                  <Typography>-</Typography>
                )}

                {/* Optional extra section for nested details */}
                {/* <Divider sx={{ my: 3 }} />

                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Related Details
                </Typography>

                <Grid container spacing={3}>
                  <DetailItem
                    label="Payment Type ID"
                    value={paymentAccount?.payment_type?.id}
                  />
                  <DetailItem
                    label="Payment Gateway ID"
                    value={paymentAccount?.payment_gateway?.id}
                  />
                  <DetailItem
                    label="Gateway Created At"
                    value={paymentAccount?.payment_gateway?.created_at}
                  />
                  <DetailItem
                    label="Payment Type Created At"
                    value={paymentAccount?.payment_type?.created_at}
                  />
                </Grid> */}
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default PaymentAccountDetails;
